import React from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, Calendar } from 'lucide-react';
import { DeliverySlot, updateDeliveryStartDate } from '@/lib/deliveryUtils';

interface DeliverySettings {
  id: string;
  start_date: string;
}

const DAYS_OF_WEEK = [
  'Söndag',
  'Måndag',
  'Tisdag',
  'Onsdag',
  'Torsdag',
  'Fredag',
  'Lördag'
];

export function DeliverySlots() {
  const [slots, setSlots] = React.useState<DeliverySlot[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [settings, setSettings] = React.useState<DeliverySettings | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [editingSlot, setEditingSlot] = React.useState<DeliverySlot | null>(null);
  const [formData, setFormData] = React.useState({
    dayOfWeek: '1', // Monday by default
    startTime: '09:00',
    endTime: '17:00',
    isActive: true,
    orderDeadlineDays: 1,
    orderDeadlineTime: '14:00'
  });

  const [startDate, setStartDate] = React.useState('');
  const [updateSuccess, setUpdateSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchSlots();
    fetchSettings();
  }, []);

  async function fetchSlots() {
    try {
      const { data, error } = await supabase
        .from('delivery_slots')
        .select('*')
        .order('day_of_week')
        .order('start_time');

      if (error) throw error;
      setSlots(data || []);
    } catch (err) {
      console.error('Error fetching delivery slots:', err);
      setError('Failed to load delivery slots');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSettings() {
    try {
      const { data, error } = await supabase
        .from('delivery_settings')
        .select('*')
        .eq('id', 'default')
        .single();

      if (error) throw error;
      setSettings(data);
      setStartDate(data.start_date);
    } catch (err) {
      console.error('Error fetching delivery settings:', err);
    }
  }

  async function handleStartDateSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUpdateSuccess(null);

    try {
      const success = await updateDeliveryStartDate(startDate);
      
      if (success) {
        setUpdateSuccess(`Startdatum uppdaterat till ${startDate}`);
        await fetchSettings();
      } else {
        throw new Error('Failed to update start date');
      }
    } catch (err) {
      console.error('Error updating start date:', err);
      setError('Failed to update start date');
    }
  }

  function handleEdit(slot: DeliverySlot) {
    setEditingSlot(slot);
    setFormData({
      dayOfWeek: slot.day_of_week.toString(),
      startTime: slot.start_time,
      endTime: slot.end_time,
      isActive: slot.is_active,
      orderDeadlineDays: slot.order_deadline_days || 1,
      orderDeadlineTime: slot.order_deadline_time || '14:00'
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    try {
      const slotData = {
        day_of_week: parseInt(formData.dayOfWeek),
        start_time: formData.startTime,
        end_time: formData.endTime,
        is_active: formData.isActive,
        order_deadline_days: formData.orderDeadlineDays,
        order_deadline_time: formData.orderDeadlineTime
      };

      if (editingSlot) {
        const { error } = await supabase
          .from('delivery_slots')
          .update(slotData)
          .eq('id', editingSlot.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('delivery_slots')
          .insert(slotData);

        if (error) throw error;
      }

      setFormData({
        dayOfWeek: '1',
        startTime: '09:00',
        endTime: '17:00',
        isActive: true,
        orderDeadlineDays: 1,
        orderDeadlineTime: '14:00'
      });
      setEditingSlot(null);
      fetchSlots();
    } catch (err) {
      console.error('Error saving delivery slot:', err);
      setError('Failed to save delivery slot');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this delivery slot?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('delivery_slots')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSlots();
    } catch (err) {
      console.error('Error deleting delivery slot:', err);
      setError('Failed to delete delivery slot');
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Leveranstider</h1>
      
      <div className="mt-8 max-w-xl">
        <form onSubmit={handleStartDateSubmit} className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            <Calendar className="inline-block h-5 w-5 mr-2 text-gray-500" />
            Startdatum för leveranser
          </h2>
          {updateSuccess && (
            <div className="rounded-md bg-green-50 p-4 mb-4">
              <div className="text-sm text-green-700">{updateSuccess}</div>
            </div>
          )}
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Första leveransdatum
              </label>
              <input
                type="date"
                id="startDate"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Spara startdatum
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Leveranser kommer att ske varje vecka från och med detta datum.
          </p>
        </form>
      </div>

      <div className="mt-8 max-w-xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <label htmlFor="dayOfWeek" className="block text-sm font-medium text-gray-700">
              Veckodag
            </label>
            <select
              id="dayOfWeek"
              required
              value={formData.dayOfWeek}
              onChange={(e) => setFormData({ ...formData, dayOfWeek: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            >
              {DAYS_OF_WEEK.map((day, index) => (
                <option key={index} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                Starttid
              </label>
              <input
                type="time"
                id="startTime"
                required
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                Sluttid
              </label>
              <input
                type="time"
                id="endTime"
                required
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-4 border-t pt-4">
            <h3 className="text-md font-medium text-gray-700 mb-3">Sista tidpunkt för beställning</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="orderDeadlineDays" className="block text-sm font-medium text-gray-700">
                  Dagar före leverans
                </label>
                <select
                  id="orderDeadlineDays"
                  value={formData.orderDeadlineDays}
                  onChange={(e) => setFormData({ ...formData, orderDeadlineDays: parseInt(e.target.value) })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                >
                  <option value="0">Samma dag</option>
                  <option value="1">1 dag före</option>
                  <option value="2">2 dagar före</option>
                  <option value="3">3 dagar före</option>
                  <option value="4">4 dagar före</option>
                  <option value="5">5 dagar före</option>
                  <option value="6">6 dagar före</option>
                  <option value="7">7 dagar före</option>
                </select>
              </div>

              <div>
                <label htmlFor="orderDeadlineTime" className="block text-sm font-medium text-gray-700">
                  Tid
                </label>
                <input
                  type="time"
                  id="orderDeadlineTime"
                  required
                  value={formData.orderDeadlineTime}
                  onChange={(e) => setFormData({ ...formData, orderDeadlineTime: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Beställningar måste göras före denna tidpunkt för att få leverans på vald dag.
            </p>
          </div>

          <div className="flex items-center mt-4">
            <input
              id="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
              Aktiv
            </label>
          </div>

          <div>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {editingSlot ? (
                'Uppdatera leveranstid'
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Lägg till leveranstid
                </>
              )}
            </button>
            {editingSlot && (
              <button
                type="button"
                onClick={() => {
                  setEditingSlot(null);
                  setFormData({
                    dayOfWeek: '1',
                    startTime: '09:00',
                    endTime: '17:00',
                    isActive: true,
                    orderDeadlineDays: 1,
                    orderDeadlineTime: '14:00'
                  });
                }}
                className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Avbryt
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="mt-8">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Veckodag
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Tid
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Beställningsdeadline
                </th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {slots.map((slot) => (
                <tr key={slot.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {DAYS_OF_WEEK[slot.day_of_week]}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {slot.start_time.slice(0, 5)} - {slot.end_time.slice(0, 5)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {slot.order_deadline_days ? (
                      <>
                        {slot.order_deadline_days === 0 
                          ? 'Samma dag' 
                          : slot.order_deadline_days === 1 
                            ? '1 dag före' 
                            : `${slot.order_deadline_days} dagar före`} 
                        {slot.order_deadline_time && (
                          <> kl {slot.order_deadline_time.slice(0, 5)}</>
                        )}
                      </>
                    ) : (
                      'Ingen deadline satt'
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      slot.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {slot.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <button
                      onClick={() => handleEdit(slot)}
                      className="text-gray-400 hover:text-gray-500 mr-4"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(slot.id)}
                      className="text-red-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}